import React, { useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import classNames from 'classnames/bind'
import { useFormatMessage } from '@babylon/intl'
import {
  Text as MedkitText,
  ButtonSimple,
  ButtonVariant,
  Link as MedkitLink,
} from '@babylon/medkit'

import { Button, VStack, Text, Link } from '@babylon/web-platform-ui'
import { useCookiePolicyLink } from '@/redux/productConfig/selectors'
import { setCookiePreference } from '@/redux/userConfig/actions'
import { useUserCookiePreference } from '@/redux/userConfig/selectors'
import styles from './CookieBanner.module.scss'
import messages from './messages'
import { useConfig } from '@/redux/partnerConfig/selectors'

const cx = classNames.bind(styles)

const CookieBanner = () => {
  const decision = useUserCookiePreference()
  const { useDnaV2Design } = useConfig()
  const translate = useFormatMessage()
  const cookiePolicyLink = useCookiePolicyLink()
  const dispatch = useDispatch()
  const bannerRef = useRef<HTMLDivElement>(null)
  const isBannerVisible = decision === null || decision === undefined

  const acceptCookies = () => {
    dispatch(setCookiePreference(true))
  }

  const declineCookies = () => {
    dispatch(setCookiePreference(false))
  }

  // In order to allow the user to operate the app without dismissing the banner
  // we must add padding to the body to ensure they can scroll to the bottom of
  // the page and access submit buttons at the bottom of forms or the footer.
  // TODO: Find a better way of adding the padding to the page.
  useEffect(() => {
    if (isBannerVisible && bannerRef.current) {
      const { height } = bannerRef.current.getBoundingClientRect()
      document.body.style.paddingBottom = `${height}px`
    } else {
      document.body.style.paddingBottom = '0px'
    }
  }, [isBannerVisible, bannerRef])

  if (!isBannerVisible) {
    return null
  }

  return (
    <div role="alert" className={cx(styles.cookieBanner)} ref={bannerRef}>
      <div className={styles.cookieBanner__inner}>
        <div className={styles.cookieBanner__content}>
          {useDnaV2Design && (
            <Text>
              {translate(messages.info, {
                a: (...chunks: any) => (
                  <Link
                    key="cookiePolicyLink"
                    isExternal
                    href={cookiePolicyLink}
                  >
                    {chunks}
                  </Link>
                ),
              })}
            </Text>
          )}
          {!useDnaV2Design && (
            <MedkitText variant="body">
              {translate(messages.info, {
                a: (...chunks: any) => (
                  <MedkitLink
                    key="cookiePolicyLink"
                    blank
                    href={cookiePolicyLink}
                  >
                    {chunks}
                  </MedkitLink>
                ),
              })}
            </MedkitText>
          )}
        </div>
        <div className={styles.buttons}>
          {useDnaV2Design && (
            <VStack spacing="2" align="flex-start">
              <Button
                onClick={acceptCookies}
                variant="primary"
                isFullWidth
                data-testid="acceptCookies"
              >
                {translate(messages.confirmText)}
              </Button>
              <Button
                onClick={declineCookies}
                isFullWidth
                variant="secondary"
                data-testid="declineCookies"
              >
                {translate(messages.declineText)}
              </Button>
            </VStack>
          )}
          {!useDnaV2Design && (
            <>
              <ButtonSimple
                className={styles.button}
                onClick={acceptCookies}
                data-testid="acceptCookies"
              >
                {translate(messages.confirmText)}
              </ButtonSimple>
              <ButtonSimple
                className={cx(styles.button)}
                onClick={declineCookies}
                variant={ButtonVariant.secondary}
                data-testid="declineCookies"
              >
                {translate(messages.declineText)}
              </ButtonSimple>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default CookieBanner
